import type { QueryFunction, QueryKey } from '@tanstack/react-query'

import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { WilkerSwadayaDataType } from '../swadaya/types'
import type {
  BidangKeahlianPnsPppkDataType,
  GetLogWilayahKerjaResponse,
  LogWilayahKerjaItem,
  PangkatGolonganPnsPppkDataType,
  PelatihanPnsPppkDataType,
  PendidikanPnsPppkDataType,
  PnsPppkDataType
} from './types'
import type {
  FilterPnsPppkValues,
  FilterSubitemPnsPppkValues,
  MutationBidangKeahlianPnsPppkValues,
  MutationPangkatGolonganPnsPppkValues,
  MutationPelatihanPnsPppkValues,
  MutationPendidikanPnsPppkValues,
  MutationPindahWilayahKerjaValues,
  MutationPnsPppkValues
} from './validation'

interface InfiniteQueryOptions<T> {
  queryKey: QueryKey
  initialPageParam: number
  getNextPageParam: (lastPage: ServiceGetAllResponse<T>) => number | undefined
  queryFn: QueryFunction<ServiceGetAllResponse<T>, QueryKey, number>
}

const PNS_PPPK_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['pns-pppk', key] as const

const getAll = (
  params?: FilterPnsPppkValues
): ServiceQueryOptionType<ServiceGetAllResponse<Array<PnsPppkDataType>>> => ({
  queryKey: PNS_PPPK_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PnsPppkDataType>>
      >('/ketenagaan/penyuluh/asn', {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllPnsPppkInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<PnsPppkDataType[]> => ({
  queryKey: PNS_PPPK_QUERY_KEY({ ...params, type: 'asn' }),
  initialPageParam: 1,
  getNextPageParam: (lastPage: ServiceGetAllResponse<PnsPppkDataType[]>) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<ServiceGetAllResponse<PnsPppkDataType[]>>(
        `/ketenagaan/penyuluh/asn`,
        {
          params: {
            ...params,
            page: pageParam
          }
        }
      )

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string
): ServiceQueryOptionType<ServiceGetByIdResponse<PnsPppkDataType>> => ({
  queryKey: PNS_PPPK_QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetByIdResponse<PnsPppkDataType>>(
        `/ketenagaan/penyuluh/asn/${id}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PnsPppkDataType>,
  MutationPnsPppkValues
> => ({
  mutationFn: async (data: MutationPnsPppkValues) => {
    try {
      // Generate wilker array based on jenis_wilker
      let wilker: string[] = []

      if (data.jenis_wilker === 'KECAMATAN') {
        // Jika Kecamatan, kirim desa
        wilker =
          data.base_kecamatan?.flatMap(kecamatan =>
            kecamatan.id_desa.map(desa => desa.value)
          ) || []
      } else if (data.jenis_wilker === 'KOTA') {
        // Jika Kota, kirim kecamatan
        wilker = data.kecamatan_ids?.map(kecamatan => kecamatan.value) || []
      } else if (data.jenis_wilker === 'PROVINSI') {
        // Jika Provinsi, kirim kota
        wilker = data.wilker_kota?.map(kota => kota.value) || []
      } else if (data.jenis_wilker === 'PUSAT') {
        // Jika Pusat

        if (data.wilker_pusat?.type === 'BPPSDMP') {
          wilker = (data.wilker_provinsi ?? []).map(p => p.value)
        } else if (data.wilker_pusat?.type === 'BBRMP') {
          wilker = [
            ...(data.wilker_pusat.data?.value ? [data.wilker_pusat.data.value] : []),
            ...(data.wilker_provinsi ?? []).map(p => p.value)
          ]
        } else if (data.wilker_pusat?.type === 'BRMP' && data.wilker_pusat?.data?.value) {
          wilker = [
            data.wilker_pusat.data.value,
            ...(data.wilker_kota ?? []).map(k => k.value)
          ]
        } else {
          wilker = []
        }
      }

      const response = await api.post<ServiceGetByIdResponse<PnsPppkDataType>>(
        '/ketenagaan/penyuluh/asn',
        {
          ...data,
          username: data.nip,
          password: data.nip.slice(-6),
          password_confirmation: data.nip.slice(-6),
          id_desa: data.id_desa.value,
          base_kecamatan: undefined,
          wilker_provinsi: undefined,
          id_status_pernikahan: data.id_status_pernikahan.value,
          id_agama: data.id_agama.value,
          bpp: data.bpp?.value,
          kecamatan:
            data.jenis_wilker === 'KECAMATAN'
              ? data.base_kecamatan?.map(kecamatan => ({
                id: kecamatan.id_kecamatan.value,
                desa: kecamatan.id_desa.map(desa => desa.value)
              }))
              : data.kecamatan_ids?.map(kecamatan => ({
                id: kecamatan.value,
                desa: undefined
              })),
          wilker,
          id_subsektor: data.subsektor.value,
          gelar: data.gelar ?? null,

          pangkat_golongan_penyuluh: {
            ...data.pangkat_golongan_penyuluh,
            jabatan: data.pangkat_golongan_penyuluh?.jabatan,
            golongan: data.pangkat_golongan_penyuluh?.golongan
          }
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PnsPppkDataType>,
  MutationPnsPppkValues
> => ({
  mutationFn: async (data: MutationPnsPppkValues) => {
    try {
      // Wilker tidak dikirim saat edit — kelola lewat pindah wilayah kerja
      const {
        jenis_wilker: _jenisWilker,
        wilker_pusat: _wilkerPusat,
        wilker_kota: _wilkerKota,
        wilker_provinsi: _wilkerProvinsi,
        base_kecamatan: _baseKecamatan,
        kecamatan_ids: _kecamatanIds,
        bpp: _bpp,
        ...rest
      } = data

      const response = await api.put<ServiceGetByIdResponse<PnsPppkDataType>>(
        `/ketenagaan/penyuluh/asn/${data.id_pns_pppk}`,
        {
          ...rest,
          password: data.password || undefined,
          password_confirmation: data.password_confirmation || undefined,
          id_desa: data.id_desa.value,
          id_status_pernikahan: data.id_status_pernikahan.value,
          id_agama: data.id_agama.value,
          id_subsektor: data.subsektor.value,
          gelar: data.gelar ?? null,

          pangkat_golongan_penyuluh: {
            ...data.pangkat_golongan_penyuluh,
            jabatan: data.pangkat_golongan_penyuluh?.jabatan,
            golongan: data.pangkat_golongan_penyuluh?.golongan
          }
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PnsPppkDataType>,
  { id_poktan: string }
> => ({
  mutationFn: async (data: { id_poktan: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PnsPppkDataType>
      >(`/ketenagaan/penyuluh/asn/${data.id_poktan}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const exportData = (filterParams?: FilterPnsPppkValues) => ({
  mutationFn: async () => {
    try {
      const response = await api.get<{
        status: string
        url?: string | null
        job_id?: string
        message?: string
        progress?: number
      }>(`/ketenagaan/penyuluh/asn/export`, {
        params: {
          ...filterParams,
          jenis: 'asn'
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

//START Pelatihan Swadaya
const getAllPelatihanPnsPppk = (
  params?: FilterSubitemPnsPppkValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PelatihanPnsPppkDataType>>
> => ({
  queryKey: PNS_PPPK_QUERY_KEY({ ...params, type: 'pelatihan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PelatihanPnsPppkDataType>>
      >(`/ketenagaan/penyuluh/asn/${params?.id_pns_pppk}/pelatihan`, {
        params: {
          ...params,
          id_pns_pppk: undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createPelatihanPnsPppk = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PelatihanPnsPppkDataType>,
  MutationPelatihanPnsPppkValues
> => ({
  mutationFn: async (data: MutationPelatihanPnsPppkValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<PelatihanPnsPppkDataType>
      >(`/ketenagaan/penyuluh/asn/${data?.id_pns_pppk}/pelatihan`, {
        ...data
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updatePelatihanPnsPppk = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PelatihanPnsPppkDataType>,
  MutationPelatihanPnsPppkValues
> => ({
  mutationFn: async (data: MutationPelatihanPnsPppkValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PelatihanPnsPppkDataType>
      >(
        `/ketenagaan/penyuluh/asn/${data?.id_pns_pppk}/pelatihan/${data.id_pelatihan}`,
        {
          ...data
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deletePelatihanPnsPppk = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PelatihanPnsPppkDataType>,
  { id_pns_pppk: string; id_data: string }
> => ({
  mutationFn: async (data: { id_pns_pppk: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PelatihanPnsPppkDataType>
      >(
        `/ketenagaan/penyuluh/asn/${data?.id_pns_pppk}/pelatihan/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

//START BIDANG KEAHLIAN PNS PPPK
const getAllBidangKeahlian = (
  params?: FilterSubitemPnsPppkValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<BidangKeahlianPnsPppkDataType>>
> => ({
  queryKey: PNS_PPPK_QUERY_KEY({ ...params, type: 'bidang-keahlian' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<BidangKeahlianPnsPppkDataType>>
      >(`/ketenagaan/penyuluh/asn/${params?.id_pns_pppk}/keahlian`, {
        params: {
          ...params,
          id_pns_pppk: undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createBidangKeahlian = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<BidangKeahlianPnsPppkDataType>,
  MutationBidangKeahlianPnsPppkValues
> => ({
  mutationFn: async (data: MutationBidangKeahlianPnsPppkValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<BidangKeahlianPnsPppkDataType>
      >(`/ketenagaan/penyuluh/asn/${data?.id_pns_pppk}/keahlian`, {
        ...data,
        id_subsektor: data.subsektor.value
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updateBidangKeahlian = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<BidangKeahlianPnsPppkDataType>,
  MutationBidangKeahlianPnsPppkValues
> => ({
  mutationFn: async (data: MutationBidangKeahlianPnsPppkValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<BidangKeahlianPnsPppkDataType>
      >(
        `/ketenagaan/penyuluh/asn/${data?.id_pns_pppk}/keahlian/${data.id_bidang_keahlian}`,
        {
          ...data,
          id_subsektor: data.subsektor.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteBidangKeahlian = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<BidangKeahlianPnsPppkDataType>,
  { id_pns_pppk: string; id_data: string }
> => ({
  mutationFn: async (data: { id_pns_pppk: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<BidangKeahlianPnsPppkDataType>
      >(
        `/ketenagaan/penyuluh/asn/${data?.id_pns_pppk}/keahlian/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

//START PENDIDIKAN PNS PPPK
const getAllPendidikan = (
  params?: FilterSubitemPnsPppkValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PendidikanPnsPppkDataType>>
> => ({
  queryKey: PNS_PPPK_QUERY_KEY({ ...params, type: 'pendidikan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PendidikanPnsPppkDataType>>
      >(`/ketenagaan/penyuluh/asn/${params?.id_pns_pppk}/pendidikan`, {
        params: {
          ...params,
          id_pns_pppk: undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createPendidikan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PendidikanPnsPppkDataType>,
  MutationPendidikanPnsPppkValues
> => ({
  mutationFn: async (data: MutationPendidikanPnsPppkValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<PendidikanPnsPppkDataType>
      >(`/ketenagaan/penyuluh/asn/${data?.id_pns_pppk}/pendidikan`, {
        ...data,
        jenjang_pendidikan: data.jenjang_pendidikan.value
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updatePendidikan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PendidikanPnsPppkDataType>,
  MutationPendidikanPnsPppkValues
> => ({
  mutationFn: async (data: MutationPendidikanPnsPppkValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PendidikanPnsPppkDataType>
      >(
        `/ketenagaan/penyuluh/asn/${data?.id_pns_pppk}/pendidikan/${data.id_pendidikan}`,
        {
          ...data,
          jenjang_pendidikan: data.jenjang_pendidikan.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deletePendidikan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PendidikanPnsPppkDataType>,
  { id_pns_pppk: string; id_data: string }
> => ({
  mutationFn: async (data: { id_pns_pppk: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PendidikanPnsPppkDataType>
      >(
        `/ketenagaan/penyuluh/asn/${data?.id_pns_pppk}/pendidikan/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllWilayahKerja = (
  params?: FilterSubitemPnsPppkValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<WilkerSwadayaDataType>>
> => ({
  queryKey: PNS_PPPK_QUERY_KEY({ ...params, type: 'wilayah-kerja' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<WilkerSwadayaDataType>>
      >(`/ketenagaan/penyuluh/asn/${params?.id_pns_pppk}/wilayah-kerja`, {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

//START BIDANG KEAHLIAN PNS PPPK
const getAllPangkatGolongan = (
  params?: FilterSubitemPnsPppkValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PangkatGolonganPnsPppkDataType>>
> => ({
  queryKey: PNS_PPPK_QUERY_KEY({ ...params, type: 'pangkat-golongan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PangkatGolonganPnsPppkDataType>>
      >(`/ketenagaan/penyuluh/asn/${params?.id_pns_pppk}/pangkat-golongan`, {
        params: {
          ...params,
          id_pns_pppk: undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createPangkatGolongan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PangkatGolonganPnsPppkDataType>,
  MutationPangkatGolonganPnsPppkValues
> => ({
  mutationFn: async (data: MutationPangkatGolonganPnsPppkValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<PangkatGolonganPnsPppkDataType>
      >(`/ketenagaan/penyuluh/asn/${data?.id_pns_pppk}/pangkat-golongan`, {
        ...data,
        id_jabatan: data.id_jabatan.value,
        id_golongan: data.id_golongan.value
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updatePangkatGolongan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PangkatGolonganPnsPppkDataType>,
  MutationPangkatGolonganPnsPppkValues
> => ({
  mutationFn: async (data: MutationPangkatGolonganPnsPppkValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PangkatGolonganPnsPppkDataType>
      >(
        `/ketenagaan/penyuluh/asn/${data?.id_pns_pppk}/pangkat-golongan/${data.id_pangkat_golongan}`,
        {
          ...data,
          id_jabatan: data.id_jabatan.value,
          id_golongan: data.id_golongan.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deletePangkatGolongan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PangkatGolonganPnsPppkDataType>,
  { id_pns_pppk: string; id_data: string }
> => ({
  mutationFn: async (data: { id_pns_pppk: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PangkatGolonganPnsPppkDataType>
      >(
        `/ketenagaan/penyuluh/asn/${data?.id_pns_pppk}/pangkat-golongan/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getLogWilker = (
  id_penyuluh: string
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<LogWilayahKerjaItem>>
> => ({
  queryKey: PNS_PPPK_QUERY_KEY({ log_wilker: id_penyuluh }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<LogWilayahKerjaItem>>
      >(`/ketenagaan/penyuluh/${id_penyuluh}/log-wilker`)

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createPindahWilayahKerja = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<LogWilayahKerjaItem>,
  { id_penyuluh: string; data: MutationPindahWilayahKerjaValues }
> => ({
  mutationFn: async (payload: {
    id_penyuluh: string
    data: MutationPindahWilayahKerjaValues
  }) => {
    try {
      const formData = new FormData()
      formData.append('id_jenis_pp', payload.data.id_jenis_pp)
      formData.append('jenis_wilker_lama', payload.data.jenis_wilker_lama)
      formData.append('jenis_wilker_baru', payload.data.jenis_wilker_baru)
      formData.append('alasan', payload.data.alasan ?? '')
      formData.append('tanggal', payload.data.tanggal)
      if (payload.data.lampiran) {
        formData.append('lampiran', payload.data.lampiran)
      }

      // Append wilker_lama array
      if (payload.data.wilker_lama && payload.data.wilker_lama.length > 0) {
        payload.data.wilker_lama.forEach(id => {
          formData.append('wilker_lama[]', id)
        })
      }

      // Append wilker_baru array
      if (payload.data.wilker_baru && payload.data.wilker_baru.length > 0) {
        payload.data.wilker_baru.forEach(id => {
          formData.append('wilker_baru[]', id)
        })
      }

      if (payload.data.penempatan?.jenis && payload.data.penempatan?.id) {
        formData.append('penempatan[jenis]', payload.data.penempatan.jenis)
        formData.append('penempatan[id]', payload.data.penempatan.id)
      }

      const response = await api.post<
        ServiceGetByIdResponse<LogWilayahKerjaItem>
      >(`/ketenagaan/penyuluh/${payload.id_penyuluh}/log-wilker`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getLogWilayahKerja = (
  id: string,
  filters?: Partial<{ page: number; per_page: number }>
): ServiceQueryOptionType<
  QueryFunction<GetLogWilayahKerjaResponse, QueryKey>,
  GetLogWilayahKerjaResponse
> => ({
  queryKey: ['pns-pppk-log-wilayah-kerja', id, filters],
  queryFn: async () => {
    try {
      const response = await api.get<GetLogWilayahKerjaResponse>(
        `/ketenagaan/penyuluh/${id}/log-wilker`,
        { params: filters }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  },
  enabled: !!id
})

export const pnsPppk = {
  queryKey: [PNS_PPPK_QUERY_KEY(undefined)[0]],
  getAll,
  getAllPnsPppkInfinite,
  getById,
  create,
  update,
  deleteData,
  exportData,
  getAllPelatihan: getAllPelatihanPnsPppk,
  createPelatihan: createPelatihanPnsPppk,
  updatePelatihan: updatePelatihanPnsPppk,
  deletePelatihan: deletePelatihanPnsPppk,

  getAllBidangKeahlian,
  createBidangKeahlian,
  updateBidangKeahlian,
  deleteBidangKeahlian,

  getAllPendidikan,
  createPendidikan,
  updatePendidikan,
  deletePendidikan,

  getAllPangkatGolongan,
  createPangkatGolongan,
  updatePangkatGolongan,
  deletePangkatGolongan,

  getAllWilayahKerja,
  getLogWilker,
  createPindahWilayahKerja,
  getLogWilayahKerja
}
